import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { 
  Share2, 
  Facebook, 
  Twitter, 
  Link as LinkIcon,
  Check,
  MessageCircle
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pinterest icon (not in lucide)
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ShareButtons = ({ 
  projectId, 
  theme, 
  variant = "buttons", // "buttons" | "icon" | "dropdown"
  className = "" 
}) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Generate share URL - points to the public preview page
  const shareUrl = `${window.location.origin}/preview/${projectId}`;
  const shareText = `Check out this coloring page I created with ColorWonder: "${theme}" 🎨 #ColorWonder #ColoringPages`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    setIsOpen(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
      setIsOpen(false);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `ColorWonder: ${theme}`,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error("Failed to share");
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  // Icon variant - single share button that opens popover
  if (variant === "icon") {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`rounded-full ${className}`}
            onClick={handleNativeShare}
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="end">
          <div className="grid gap-1">
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4 text-blue-600" />
              Facebook
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4 text-sky-500" />
              Twitter / X
            </button>
            <button
              onClick={() => handleShare('pinterest')}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="share-pinterest"
            >
              <PinterestIcon className="w-4 h-4 text-red-600" />
              Pinterest
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="share-whatsapp"
            >
              <WhatsAppIcon className="w-4 h-4 text-green-600" />
              WhatsApp
            </button>
            <div className="border-t my-1" />
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="copy-link"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4 text-slate-600" />
                  Copy Link
                </>
              )}
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Buttons variant - horizontal row of share buttons
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-slate-500 mr-1">Share:</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('facebook')}
        className="h-9 w-9 p-0 rounded-full hover:bg-blue-50 hover:border-blue-200"
        data-testid="share-facebook"
      >
        <Facebook className="w-4 h-4 text-blue-600" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('twitter')}
        className="h-9 w-9 p-0 rounded-full hover:bg-sky-50 hover:border-sky-200"
        data-testid="share-twitter"
      >
        <Twitter className="w-4 h-4 text-sky-500" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('pinterest')}
        className="h-9 w-9 p-0 rounded-full hover:bg-red-50 hover:border-red-200"
        data-testid="share-pinterest"
      >
        <PinterestIcon className="w-4 h-4 text-red-600" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('whatsapp')}
        className="h-9 w-9 p-0 rounded-full hover:bg-green-50 hover:border-green-200"
        data-testid="share-whatsapp"
      >
        <WhatsAppIcon className="w-4 h-4 text-green-600" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyLink}
        className="h-9 w-9 p-0 rounded-full hover:bg-slate-100"
        data-testid="copy-link"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <LinkIcon className="w-4 h-4 text-slate-600" />
        )}
      </Button>
    </div>
  );
};

export default ShareButtons;
